import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield,
  ArrowLeft,
  Loader2,
  LogOut,
  Megaphone,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Image,
  Youtube,
  Instagram,
  Eye,
  EyeOff,
  GripVertical,
  LayoutGrid,
  Square,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEDIA_TYPES = [
  { value: "image", label: "Imagem", icon: Image },
  { value: "youtube", label: "YouTube", icon: Youtube },
  { value: "instagram", label: "Instagram", icon: Instagram },
];

function getYoutubeId(url) {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function getInstagramId(url) {
  const match = url.match(/instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function MediaPreview({ mediaType, mediaUrl }) {
  if (!mediaUrl) return null;

  if (mediaType === "image") {
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        <img src={mediaUrl} alt="Preview" className="w-full h-40 object-cover" />
      </div>
    );
  }
  if (mediaType === "youtube") {
    const vid = getYoutubeId(mediaUrl);
    if (!vid) return <p className="text-sm text-red-500">URL do YouTube invalida</p>;
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200 aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${vid}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube preview"
        />
      </div>
    );
  }
  if (mediaType === "instagram") {
    const postId = getInstagramId(mediaUrl);
    if (!postId) return <p className="text-sm text-red-500">URL do Instagram invalida</p>;
    return (
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <iframe
          src={`https://www.instagram.com/p/${postId}/embed`}
          className="w-full"
          style={{ minHeight: 300 }}
          allowFullScreen
          title="Instagram preview"
        />
      </div>
    );
  }
  return null;
}

function PromoForm({ promo, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(promo?.title || "");
  const [description, setDescription] = useState(promo?.description || "");
  const [mediaType, setMediaType] = useState(promo?.media_type || "image");
  const [mediaUrl, setMediaUrl] = useState(promo?.media_url || "");
  const [linkUrl, setLinkUrl] = useState(promo?.link_url || "");
  const [linkText, setLinkText] = useState(promo?.link_text || "Saiba mais");
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Auto-crop image to 16:9 and resize
  const processImage = (file) => {
    return new Promise((resolve) => {
      const img = document.createElement("img");
      img.onload = () => {
        const TARGET_W = 800;
        const TARGET_H = 450; // 16:9
        const canvas = document.createElement("canvas");
        canvas.width = TARGET_W;
        canvas.height = TARGET_H;
        const ctx = canvas.getContext("2d");

        // Calculate crop area (center crop to 16:9)
        const srcRatio = img.width / img.height;
        const targetRatio = TARGET_W / TARGET_H;
        let sx, sy, sw, sh;
        if (srcRatio > targetRatio) {
          // Image is wider - crop sides
          sh = img.height;
          sw = img.height * targetRatio;
          sx = (img.width - sw) / 2;
          sy = 0;
        } else {
          // Image is taller - crop top/bottom
          sw = img.width;
          sh = img.width / targetRatio;
          sx = 0;
          sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_W, TARGET_H);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 10MB");
      return;
    }
    setUploading(true);
    try {
      const croppedBlob = await processImage(file);
      const formData = new FormData();
      formData.append("file", croppedBlob, "promo-banner.jpg");

      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API_URL}/api/admin/upload-promo-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(res.data.url);
      toast.success("Imagem enviada e enquadrada automaticamente!");
    } catch {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    onSave({ title, description, media_type: mediaType, media_url: mediaUrl, link_url: linkUrl, link_text: linkText });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-gray-900 text-lg">
          {promo ? "Editar Propaganda" : "Nova Propaganda"}
        </h3>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-700">Titulo *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Organize seus comprovantes"
          className="bg-gray-50 border-gray-300"
          data-testid="promo-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-gray-700">Descricao</Label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Texto descritivo da propaganda..."
          rows={3}
          className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
          data-testid="promo-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-gray-700">Tipo de Midia</Label>
        <div className="flex gap-2">
          {MEDIA_TYPES.map((mt) => {
            const Icon = mt.icon;
            return (
              <button
                key={mt.value}
                type="button"
                onClick={() => { setMediaType(mt.value); setMediaUrl(""); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  mediaType === mt.value
                    ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                    : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
                data-testid={`promo-media-type-${mt.value}`}
              >
                <Icon className="w-4 h-4" />
                {mt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-gray-700">
          {mediaType === "image" ? "Imagem" : mediaType === "youtube" ? "URL do YouTube" : "URL do Instagram"}
        </Label>
        {mediaType === "image" ? (
          <div className="space-y-3">
            <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
              onClick={() => !uploading && fileInputRef.current?.click()}
              data-testid="promo-image-upload-area"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                  <p className="text-sm text-indigo-600 font-medium">Enviando e enquadrando...</p>
                </div>
              ) : mediaUrl ? (
                <div className="space-y-3">
                  <img src={mediaUrl} alt="Preview" className="w-full rounded-lg object-cover" style={{ maxHeight: 200 }} />
                  <p className="text-xs text-gray-500">Clique para trocar a imagem</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-gray-400" />
                  <p className="text-sm font-medium text-gray-600">Clique para enviar imagem</p>
                  <p className="text-xs text-gray-400">A imagem sera enquadrada automaticamente para 16:9 (800x450px)</p>
                  <p className="text-xs text-gray-400">JPG, PNG, WebP - ate 10MB</p>
                </div>
              )}
            </div>
            <div>
              <Label className="text-gray-500 text-xs">Ou cole a URL da imagem:</Label>
              <Input
                value={mediaUrl.startsWith("data:") ? "" : (mediaUrl.includes("/api/") ? "" : mediaUrl)}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="https://exemplo.com/imagem.jpg"
                className="bg-gray-50 border-gray-300 mt-1"
              />
            </div>
          </div>
        ) : (
          <Input
            value={mediaUrl}
            onChange={(e) => setMediaUrl(e.target.value)}
            placeholder={mediaType === "youtube" ? "https://youtube.com/watch?v=..." : "https://instagram.com/p/..."}
            className="bg-gray-50 border-gray-300"
            data-testid="promo-media-url-input"
          />
        )}
        {mediaUrl && <MediaPreview mediaType={mediaType} mediaUrl={mediaUrl} />}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-gray-700">Link (opcional)</Label>
          <Input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            className="bg-gray-50 border-gray-300"
            data-testid="promo-link-url-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-gray-700">Texto do Link</Label>
          <Input
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="Saiba mais"
            className="bg-gray-50 border-gray-300"
            data-testid="promo-link-text-input"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="border-gray-300">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {promo ? "Salvar Alterações" : "Criar Propaganda"}
        </Button>
      </div>
    </form>
  );
}

export const AdminPromotionsPage = () => {
  const navigate = useNavigate();
  const { token: adminToken, isAuthenticated, logout } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [displayMode, setDisplayMode] = useState("carrossel");
  const [showForm, setShowForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingMode, setChangingMode] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const loadPromotions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/promotions`, { headers });
      setPromotions(res.data.promotions || res.data);
      if (res.data.display_mode) setDisplayMode(res.data.display_mode);
    } catch {
      toast.error("Erro ao carregar propagandas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) loadPromotions();
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const handleModeChange = async (newMode) => {
    setChangingMode(true);
    try {
      await axios.put(`${API_URL}/api/admin/promotions/display-mode`, { mode: newMode }, { headers });
      setDisplayMode(newMode);
      toast.success(newMode === "carrossel" ? "Modo carrossel ativado!" : "Modo propaganda unitaria ativado!");
      loadPromotions();
    } catch {
      toast.error("Erro ao alterar modo");
    } finally {
      setChangingMode(false);
    }
  };

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/promotions`, data, { headers });
      toast.success("Propaganda criada!");
      setShowForm(false);
      loadPromotions();
    } catch {
      toast.error("Erro ao criar propaganda");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editingPromo) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/promotions/${editingPromo.id}`, data, { headers });
      toast.success("Propaganda atualizada!");
      setEditingPromo(null);
      loadPromotions();
    } catch {
      toast.error("Erro ao atualizar propaganda");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (promo) => {
    try {
      await axios.put(`${API_URL}/api/admin/promotions/${promo.id}`, { active: !promo.active }, { headers });
      setPromotions((prev) => prev.map((p) => p.id === promo.id ? { ...p, active: !p.active } : p));
      toast.success(promo.active ? "Propaganda desativada" : "Propaganda ativada!");
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm("Tem certeza que deseja excluir esta propaganda?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/promotions/${promoId}`, { headers });
      toast.success("Propaganda excluida!");
      loadPromotions();
    } catch {
      toast.error("Erro ao excluir propaganda");
    }
  };

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-promotions-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                <Megaphone className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-heading text-xl font-bold text-gray-900">Propagandas</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4" />
            Sair
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Display Mode Selector */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6" data-testid="display-mode-card">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-5 h-5 text-indigo-500" />
            <h3 className="font-bold text-gray-900">Modo de Exibicao</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => handleModeChange("unitaria")}
              disabled={changingMode}
              className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                displayMode === "unitaria"
                  ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
              data-testid="mode-unitaria"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                displayMode === "unitaria" ? "bg-indigo-500 text-white" : "bg-gray-100 text-gray-400"
              }`}>
                <Square className="w-5 h-5" />
              </div>
              <div>
                <p className={`font-semibold text-sm ${displayMode === "unitaria" ? "text-indigo-700" : "text-gray-700"}`}>
                  Propaganda Unitaria
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Apenas uma propaganda ativa por vez</p>
              </div>
              {displayMode === "unitaria" && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
              )}
            </button>
            <button
              onClick={() => handleModeChange("carrossel")}
              disabled={changingMode}
              className={`relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                displayMode === "carrossel"
                  ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
              data-testid="mode-carrossel"
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                displayMode === "carrossel" ? "bg-indigo-500 text-white" : "bg-gray-100 text-gray-400"
              }`}>
                <LayoutGrid className="w-5 h-5" />
              </div>
              <div>
                <p className={`font-semibold text-sm ${displayMode === "carrossel" ? "text-indigo-700" : "text-gray-700"}`}>
                  Carrossel
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Varias propagandas ativas, rodando automaticamente</p>
              </div>
              {displayMode === "carrossel" && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </div>
              )}
            </button>
          </div>
          {displayMode === "unitaria" && (
            <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              No modo unitario, ativar uma propaganda desativa automaticamente as demais.
            </p>
          )}
          {displayMode === "carrossel" && (
            <p className="text-xs text-indigo-600 mt-3 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              No modo carrossel, ative varias propagandas para que rodem automaticamente na tela.
            </p>
          )}
        </div>

        {/* Active count info */}
        {promotions.length > 0 && (
          <div className="flex items-center justify-between mb-4 px-1">
            <p className="text-sm text-gray-500">
              <span className="font-semibold text-emerald-600">{promotions.filter(p => p.active).length}</span> ativa(s) de {promotions.length} propaganda(s)
              {displayMode === "carrossel" && promotions.filter(p => p.active).length > 1 && (
                <span className="text-indigo-500 ml-1">(carrossel)</span>
              )}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-gray-500">
              Gerencie as propagandas exibidas na página principal. Escolha o modo acima e ative com a chave.
            </p>
          </div>
          <Button
            onClick={() => { setShowForm(true); setEditingPromo(null); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            data-testid="add-promotion-button"
          >
            <Plus className="w-4 h-4" />
            Nova Propaganda
          </Button>
        </div>

        {/* Form */}
        {(showForm || editingPromo) && (
          <div className="mb-6">
            <PromoForm
              promo={editingPromo}
              onSave={editingPromo ? handleUpdate : handleCreate}
              onCancel={() => { setShowForm(false); setEditingPromo(null); }}
              saving={saving}
            />
          </div>
        )}

        {/* Promotions List */}
        {promotions.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma propaganda cadastrada</h3>
            <p className="text-gray-500 mb-6">Crie sua primeira propaganda para exibir na página principal.</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              Criar Primeira Propaganda
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {promotions.map((promo) => (
              <div
                key={promo.id}
                className={`bg-white rounded-xl border shadow-sm p-5 transition-all ${
                  promo.active ? "border-emerald-300" : "border-gray-200 opacity-70"
                }`}
                data-testid={`promo-card-${promo.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Media thumbnail */}
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200">
                    {promo.media_type === "image" && promo.media_url ? (
                      <img src={promo.media_url} alt={promo.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {promo.media_type === "youtube" ? (
                          <Youtube className="w-8 h-8 text-red-400" />
                        ) : promo.media_type === "instagram" ? (
                          <Instagram className="w-8 h-8 text-pink-400" />
                        ) : (
                          <Image className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 truncate">{promo.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        promo.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
                      }`}>
                        {promo.active ? "ATIVA" : "INATIVA"}
                      </span>
                    </div>
                    {promo.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">{promo.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        {promo.media_type === "image" && <Image className="w-3 h-3" />}
                        {promo.media_type === "youtube" && <Youtube className="w-3 h-3" />}
                        {promo.media_type === "instagram" && <Instagram className="w-3 h-3" />}
                        {promo.media_type === "image" ? "Imagem" : promo.media_type === "youtube" ? "YouTube" : "Instagram"}
                      </span>
                      {promo.link_url && (
                        <a href={promo.link_url} target="_blank" rel="noopener noreferrer" className="text-indigo-500 hover:underline truncate max-w-[200px]">
                          {promo.link_url}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                      checked={promo.active}
                      onCheckedChange={() => handleToggle(promo)}
                      data-testid={`promo-toggle-${promo.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingPromo(promo); setShowForm(false); }}
                      className="text-gray-400 hover:text-indigo-600"
                      data-testid={`promo-edit-${promo.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(promo.id)}
                      className="text-gray-400 hover:text-rose-600"
                      data-testid={`promo-delete-${promo.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPromotionsPage;
